import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatsBar } from "../components/StatsBar";
import { ReportCard } from "../components/ReportCard";
import { ReportDetailModal } from "../components/ReportDetailModal";
import { PLATFORMS, GAME_MODES, CHEAT_TYPES, GLITCH_CHEAT_TYPE } from "../lib/constants";
import { SponsorBanner } from "../components/SponsorBanner";
import { BanTicker } from "../components/BanTicker";
import { HeroBanner } from "../components/HeroBanner";
import { MostWanted } from "../components/MostWanted";
import { HunterOfMonth } from "../components/HunterOfMonth";
import { SupporterLeaderboard } from "../components/SupporterLeaderboard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, Crosshair, ShieldAlert, Loader2, Bug, ShieldQuestion } from "lucide-react";
import { toast } from "sonner";

const HERO_BG = "https://images.unsplash.com/photo-1519432473078-0151c4f90335?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

const FilterChip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
      active ? "border-[#00F0FF] bg-[#00F0FF]/15 text-[#00F0FF]" : "border-[#1E293F] bg-[#141B2D] text-[#94A3B8] hover:border-[#00F0FF]/40 hover:text-white"
    }`}
  >
    {children}
  </button>
);

export default function Home() {
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [platform, setPlatform] = useState("");
  const [gameMode, setGameMode] = useState("");
  const [cheatType, setCheatType] = useState("");
  const [sort, setSort] = useState("top");
  const [tab, setTab] = useState("wall");
  const [streamersOnly, setStreamersOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const [refreshStats, setRefreshStats] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const isSuspects = tab === "suspects";
      const params = { status: isSuspects ? "suspect" : "all", sort };
      if (search) params.search = search;
      if (platform) params.platform = platform;
      if (gameMode) params.game_mode = gameMode;
      const effectiveCheat = tab === "glitch" ? GLITCH_CHEAT_TYPE : cheatType;
      if (effectiveCheat) params.cheat_type = effectiveCheat;
      if (streamersOnly && tab !== "suspects") params.streamers = true;
      const { data } = await api.get("/reports", { params });
      const counts = {};
      data.forEach((r) => { const k = `${r.platform}::${r.gamertag.toLowerCase()}`; counts[k] = (counts[k] || 0) + 1; });
      const annotated = data.map((r) => ({ ...r, is_serial: counts[`${r.platform}::${r.gamertag.toLowerCase()}`] >= 2 }));
      setReports(annotated);
    } catch {
      toast.error("Failed to load reports.");
    } finally {
      setLoading(false);
    }
  }, [search, platform, gameMode, cheatType, sort, tab, streamersOnly]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const handleUpvote = async (report) => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/reports/${report.id}/upvote`);
      setReports((rs) => rs.map((r) => (r.id === data.id ? { ...data, is_serial: r.is_serial } : r)));
      if (selected?.id === data.id) setSelected({ ...data, is_serial: selected.is_serial });
      setRefreshStats((n) => n + 1);
    } catch {
      toast.error("Could not register your vote.");
    }
  };

  const handleDownvote = async (report) => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/reports/${report.id}/downvote`);
      setReports((rs) => rs.map((r) => (r.id === data.id ? { ...data, is_serial: r.is_serial } : r)));
      if (selected?.id === data.id) setSelected({ ...data, is_serial: selected.is_serial });
    } catch {
      toast.error("Could not register your vote.");
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Delete the post on "${report.gamertag}"? This can't be undone.`)) return;
    try {
      await api.delete(`/reports/${report.id}`);
      setReports((rs) => rs.filter((r) => r.id !== report.id));
      setSelected(null);
      setRefreshStats((n) => n + 1);
      toast.success("Post deleted.");
    } catch {
      toast.error("Could not delete the post.");
    }
  };

  const handleReact = async (report, emoji) => {
    if (!user) return openAuth();
    try {
      const { data } = await api.post(`/reports/${report.id}/react`, { emoji });
      setReports((rs) => rs.map((r) => (r.id === data.id ? { ...data, is_serial: r.is_serial } : r)));
      if (selected?.id === data.id) setSelected({ ...data, is_serial: selected.is_serial });
    } catch { toast.error("Reaction failed."); }
  };

  const openDetail = async (report) => {
    setSelected(report);
    try {
      const { data } = await api.get(`/reports/${report.id}`);
      setSelected(data);
    } catch (err) { console.error("Failed to load full case details; showing card data:", err); }
  };

  return (
    <div>
      <BanTicker />
      <HeroBanner />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1E293F]">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#090C15]/70 via-[#090C15]/85 to-[#090C15]" />
          <div className="absolute inset-0 grid-texture opacity-40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 sm:pt-20">
          <div className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-[#FF2A55]/30 bg-[#FF2A55]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#FF2A55]">
            <ShieldAlert size={13} /> NBA 2K Community Anti-Cheat
          </div>
          <h1 className="animate-fade-up mt-5 max-w-3xl font-heading text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            The <span className="text-[#FF2A55]">Wall of Shame</span> for 2K cheaters.
          </h1>
          <p className="animate-fade-up mt-4 max-w-xl text-base text-[#94A3B8] sm:text-lg" style={{ animationDelay: "160ms" }}>
            Upload proof. Real modders vet every report. Confirmed cheaters get sent straight to the 2K dev team for manual bans.
          </p>
          <div className="animate-fade-up mt-7 flex flex-wrap gap-3" style={{ animationDelay: "240ms" }}>
            <Button
              data-testid="hero-report-btn"
              onClick={() => (user ? navigate("/submit") : openAuth())}
              className="bg-[#FF2A55] font-bold text-white hover:bg-[#FF2A55]/90 glow-crimson"
            >
              <Crosshair size={17} className="mr-2" /> Report a Cheater
            </Button>
            <Button
              data-testid="hero-browse-btn"
              onClick={() => document.getElementById("wall")?.scrollIntoView({ behavior: "smooth" })}
              variant="outline"
              className="border-[#1E293F] bg-transparent text-white hover:bg-[#141B2D] hover:text-white"
            >
              Browse the Wall
            </Button>
          </div>
          <div className="animate-fade-up mt-10" style={{ animationDelay: "320ms" }}>
            <StatsBar refreshKey={refreshStats} />
          </div>
        </div>
      </section>

      <HunterOfMonth />

      <MostWanted onOpen={openDetail} />

      {/* Wall */}
      <section id="wall" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <SponsorBanner />
        <div className="mb-6 flex items-center gap-2 border-b border-[#1E293F]">
          <button
            data-testid="tab-wall"
            onClick={() => setTab("wall")}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold transition-colors ${tab === "wall" ? "border-[#FF2A55] text-white" : "border-transparent text-[#64748B] hover:text-white"}`}
          >
            <Crosshair size={16} /> Wall of Shame
          </button>
          <button
            data-testid="tab-glitch"
            onClick={() => setTab("glitch")}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold transition-colors ${tab === "glitch" ? "border-[#00FF87] text-white" : "border-transparent text-[#64748B] hover:text-white"}`}
          >
            <Bug size={16} /> Glitch Abusers
          </button>
          <button
            data-testid="tab-suspects"
            onClick={() => setTab("suspects")}
            className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-bold transition-colors ${tab === "suspects" ? "border-[#F97316] text-white" : "border-transparent text-[#64748B] hover:text-white"}`}
          >
            <ShieldQuestion size={16} /> Suspects
          </button>
        </div>

        <div className="mb-6 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
              {tab === "glitch" ? "Glitch Abusers" : tab === "suspects" ? "Suspects (Under Review)" : "Wall of Shame"} <span className="font-mono text-base text-[#64748B]">({reports.length})</span>
            </h2>
            <div className="relative w-full max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
              <Input
                data-testid="search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search gamertag..."
                className="border-[#1E293F] bg-[#141B2D] pl-9 text-white placeholder:text-[#64748B]"
              />
            </div>
          </div>

          {tab === "glitch" && (
            <p className="text-sm text-[#94A3B8]" data-testid="glitch-desc">
              Players exploiting in-game glitches (animation, stamina, no-loss, etc.) for an unfair advantage — reported and vetted by the community.
            </p>
          )}

          {tab === "suspects" && (
            <div className="flex items-start gap-2 rounded-lg border border-[#F97316]/30 bg-[#F97316]/[0.08] p-3 text-sm leading-relaxed text-[#F59E0B]" data-testid="suspects-disclaimer">
              <ShieldQuestion size={18} className="mt-px shrink-0" />
              <span>These are <strong>community posts on suspected cheaters</strong> — <strong>not yet confirmed or vetted by moderators.</strong> Upvote if you've faced them, or press "legit" if you think someone's wrongly listed. Moderators review the top suspects and make the final call.</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <FilterChip active={!platform && !gameMode && !cheatType} onClick={() => { setPlatform(""); setGameMode(""); setCheatType(""); }} testid="filter-all">All</FilterChip>
            <span className="mx-1 h-4 w-px bg-[#1E293F]" />
            {PLATFORMS.map((p) => (
              <FilterChip key={p} active={platform === p} onClick={() => setPlatform(platform === p ? "" : p)} testid={`filter-platform-${p}`}>{p}</FilterChip>
            ))}
            <span className="mx-1 h-4 w-px bg-[#1E293F]" />
            {GAME_MODES.map((m) => (
              <FilterChip key={m} active={gameMode === m} onClick={() => setGameMode(gameMode === m ? "" : m)} testid={`filter-mode-${m}`}>{m}</FilterChip>
            ))}
          </div>

          {(tab === "wall" || tab === "suspects") && (
            <div className="flex flex-wrap items-center gap-2">
              {CHEAT_TYPES.map((c) => (
                <FilterChip key={c} active={cheatType === c} onClick={() => setCheatType(cheatType === c ? "" : c)} testid={`filter-cheat-${c}`}>{c}</FilterChip>
              ))}
              <span className="ml-auto flex items-center gap-1">
                {tab !== "suspects" && (
                  <FilterChip active={streamersOnly} onClick={() => setStreamersOnly(!streamersOnly)} testid="filter-streamers">🎥 Streamers</FilterChip>
                )}
                <FilterChip active={sort === "top"} onClick={() => setSort("top")} testid="sort-top">🔥 Top</FilterChip>
                <FilterChip active={sort === "recent"} onClick={() => setSort("recent")} testid="sort-recent">Recent</FilterChip>
              </span>
            </div>
          )}
          {tab === "glitch" && (
            <div className="flex items-center gap-1">
              <FilterChip active={streamersOnly} onClick={() => setStreamersOnly(!streamersOnly)} testid="filter-streamers-glitch">🎥 Streamers</FilterChip>
              <FilterChip active={sort === "top"} onClick={() => setSort("top")} testid="sort-top-glitch">🔥 Top</FilterChip>
              <FilterChip active={sort === "recent"} onClick={() => setSort("recent")} testid="sort-recent-glitch">Recent</FilterChip>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-24 text-[#64748B]"><Loader2 className="animate-spin" /></div>
        ) : reports.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#1E293F] bg-[#0F1424] py-20 text-center" data-testid="empty-wall">
            <ShieldAlert className="mx-auto mb-3 text-[#334155]" size={36} />
            <p className="text-[#94A3B8]">{tab === "glitch" ? "No glitch abusers reported yet." : tab === "suspects" ? "No community suspects yet. Be the first to call one out." : "No cheaters match your filters yet."}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="reports-grid">
            {reports.map((r, i) => (
              <ReportCard key={r.id} report={r} index={i} onUpvote={handleUpvote} onDownvote={handleDownvote} onOpen={openDetail} />
            ))}
          </div>
        )}
      </section>

      <SupporterLeaderboard />

      <ReportDetailModal report={selected} open={!!selected} onClose={() => setSelected(null)} onUpvote={handleUpvote} onDownvote={handleDownvote} onReact={handleReact} onDelete={handleDelete} />
    </div>
  );
}
